import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { motion, useInView } from "framer-motion";

// Backend Error Messages with Translations
const ERROR_MESSAGES = {
  // IP Blocked
  "Your IP has been blocked due to suspicious activity. Please try again later.": {
    en: "Your IP has been blocked due to suspicious activity. Please try again later.",
    fr: "Votre IP a été bloquée en raison d'une activité suspecte. Veuillez réessayer plus tard.",
    ar: "تم حظر عنوان IP الخاص بك بسبب نشاط مريب. يرجى محاولة مجددًا لاحقًا."
  },
  // Invalid request format
  "Invalid request format": {
    en: "Invalid request format. Please check your input.",
    fr: "Format de demande invalide. Veuillez vérifier votre saisie.",
    ar: "تنسيق الطلب غير صحيح. يرجى التحقق من إدخالك."
  },
  // Email validation errors
  "Email is required": {
    en: "Email is required",
    fr: "L'email est obligatoire",
    ar: "البريد الإلكتروني مطلوب"
  },
  "Please provide a valid email address": {
    en: "Please provide a valid email address",
    fr: "Veuillez fournir une adresse e-mail valide",
    ar: "يرجى تقديم عنوان بريد إلكتروني صحيح"
  },
  "Email already used for contact": {
    en: "This email has already been used. Please use a different email address.",
    fr: "Cet e-mail a déjà été utilisé. Veuillez utiliser une adresse e-mail différente.",
    ar: "تم استخدام هذا البريد الإلكتروني بالفعل. يرجى استخدام عنوان بريد إلكتروني مختلف."
  },
  // Phone validation errors
  "Phone is required": {
    en: "Phone number is required",
    fr: "Le numéro de téléphone est obligatoire",
    ar: "رقم الهاتف مطلوب"
  },
  "Please provide a valid phone number": {
    en: "Please provide a valid phone number",
    fr: "Veuillez fournir un numéro de téléphone valide",
    ar: "يرجى تقديم رقم هاتف صحيح"
  },
  "Phone number too long": {
    en: "Phone number is too long",
    fr: "Le numéro de téléphone est trop long",
    ar: "رقم الهاتف طويل جدًا"
  },
  // Message validation errors
  "Invalid input detected": {
    en: "Invalid characters detected in your message. Please remove any suspicious content.",
    fr: "Des caractères invalides ont été détectés dans votre message. Veuillez supprimer tout contenu suspect.",
    ar: "تم اكتشاف أحرف غير صحيحة في رسالتك. يرجى إزالة أي محتوى مريب."
  },
  "Message must be at least 10 characters": {
    en: "Message must be at least 10 characters long",
    fr: "Le message doit contenir au moins 10 caractères",
    ar: "يجب أن تكون الرسالة بطول 10 أحرف على الأقل"
  },
  "Message is too long": {
    en: "Message is too long (maximum 5000 characters)",
    fr: "Le message est trop long (maximum 5000 caractères)",
    ar: "الرسالة طويلة جدًا (الحد الأقصى 5000 حرف)"
  },
  // Name validation errors
  "Invalid name format": {
    en: "Invalid name format. Please enter a valid name.",
    fr: "Format de nom invalide. Veuillez entrer un nom valide.",
    ar: "تنسيق الاسم غير صحيح. يرجى إدخال اسم صحيح."
  },
  "Name is required": {
    en: "Name is required",
    fr: "Le nom est obligatoire",
    ar: "الاسم مطلوب"
  },
  // Email service errors
  "Email service unavailable": {
    en: "Email service is currently unavailable. Please try again later.",
    fr: "Le service de courrier électronique n'est actuellement pas disponible. Veuillez réessayer plus tard.",
    ar: "خدمة البريد الإلكتروني غير متاحة حاليًا. يرجى محاولة مجددًا لاحقًا."
  },
  // Rate limit errors
  "You have reached the maximum number of emails (3). Please try again later.": {
    en: "You have reached the maximum number of messages (3). Please try again later.",
    fr: "Vous avez atteint le nombre maximum de messages (3). Veuillez réessayer plus tard.",
    ar: "لقد وصلت إلى الحد الأقصى من الرسائل (3). يرجى محاولة مجددًا لاحقًا."
  },
  "Too many requests. Please try again later.": {
    en: "Too many requests. Please try again later.",
    fr: "Trop de demandes. Veuillez réessayer plus tard.",
    ar: "عدد كبير جدًا من الطلبات. يرجى محاولة مجددًا لاحقًا."
  },
  // Generic errors
  "Failed to send your message": {
    en: "Failed to send your message. Please try again later.",
    fr: "Impossible d'envoyer votre message. Veuillez réessayer plus tard.",
    ar: "فشل إرسال رسالتك. يرجى محاولة مجددًا لاحقًا."
  }
};

// POST Cooldown constant (matching backend)
const POST_COOLDOWN = 10 * 1000; // 10 seconds cooldown between POSTs

// Reusable fade-up animation wrapper - MOVED OUTSIDE to prevent re-creation
const FadeUp = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Helper function to get translated error message
const getErrorMessage = (backendError, language) => {
  if (!backendError) return "An error occurred. Please try again later.";
  
  // Check exact match first
  if (ERROR_MESSAGES[backendError]) {
    return ERROR_MESSAGES[backendError][language] || ERROR_MESSAGES[backendError]["en"];
  }
  
  // Check for partial matches
  for (const [key, translations] of Object.entries(ERROR_MESSAGES)) {
    if (backendError.includes(key) || key.includes(backendError)) {
      return translations[language] || translations["en"];
    }
  }
  
  // Default fallback
  const fallbackMessages = {
    en: "An error occurred while sending your message. Please try again later.",
    fr: "Une erreur s'est produite lors de l'envoi de votre message. Veuillez réessayer plus tard.",
    ar: "حدث خطأ أثناء إرسال رسالتك. يرجى محاولة مجددًا لاحقًا."
  };
  
  return fallbackMessages[language] || fallbackMessages["en"];
};

function Contact() {
  const language = useSelector((state) => state.language);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // Cooldown timer effect
  React.useEffect(() => {
    if (cooldownRemaining <= 0) return;

    const timer = setInterval(() => {
      setCooldownRemaining((prev) => {
        if (prev <= 1000) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldownRemaining]);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (message) {
      setMessage("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    // Check cooldown
    if (cooldownRemaining > 0) {
      const seconds = Math.ceil(cooldownRemaining / 1000);
      const cooldownMsg = language === "ar"
        ? `❌ يرجى الانتظار ${seconds} ثانية قبل إرسال رسالة أخرى`
        : language === "fr"
        ? `❌ Veuillez attendre ${seconds} secondes avant d'envoyer un autre message`
        : `❌ Please wait ${seconds} seconds before sending another message`;
      setMessage(cooldownMsg);
      setLoading(false);
      return;
    }

    if (!formData.firstName || !formData.email || !formData.message) {
      const validationMsg = language === "ar"
        ? "❌ يرجى ملء جميع الحقول المطلوبة"
        : language === "fr"
        ? "❌ Veuillez remplir tous les champs obligatoires"
        : "❌ Please fill in all required fields";
      setMessage(validationMsg);
      setLoading(false);
      return;
    }

    if (formData.message.length < 10) {
      const msgError = language === "ar"
        ? "❌ " + ERROR_MESSAGES["Message must be at least 10 characters"]["ar"]
        : language === "fr"
        ? "❌ " + ERROR_MESSAGES["Message must be at least 10 characters"]["fr"]
        : "❌ " + ERROR_MESSAGES["Message must be at least 10 characters"]["en"];
      setMessage(msgError);
      setLoading(false);
      return;
    }

    try {
      // Combining details for the simplified backend
      const combinedFullName = `${formData.firstName} ${formData.lastName}`.trim();
      
      const response = await fetch("https://api.raqibapp.com/public/email/contact-us", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: combinedFullName,
          email: formData.email,
          message: formData.message,
          phone: formData.phone,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        // Get translated error message from backend response
        const langKey = language === "ar" ? "ar" : language === "fr" ? "fr" : "en";
        const translatedError = getErrorMessage(result.error, langKey);
        throw new Error(translatedError);
      }

      const successMsg = language === "ar"
        ? "✓ تم إرسال رسالتك بنجاح!\n\nشكراً لتواصلك معنا. سنرد عليك قريباً."
        : language === "fr"
        ? "✓ Votre message a été envoyé avec succès!\n\nMerci de nous avoir contactés. Nous vous répondrons bientôt."
        : "✓ Your message has been sent successfully!\n\nThank you for contacting us. We'll get back to you soon.";

      setMessage(successMsg);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: "",
      });

      // Set cooldown after successful submission
      setCooldownRemaining(POST_COOLDOWN);

      setTimeout(() => {
        setMessage("");
      }, 5000);
    } catch (error) {
      console.error("Error sending message:", error);

      // Use the translated error message directly
      const errorMsg = "❌ " + error.message;
      setMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const isAr = language === "ar";
  const isFr = language === "fr";
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [emailInput, setEmailInput] = useState("");

  const faqItems = [
    {
      questionEn: "What makes RAQIB different from other apps?",
      questionFr: "Qu'est-ce qui rend RAQIB différent des autres applications?",
      questionAr: "ما الذي يجعل RAQIB مختلفًا عن التطبيقات الأخرى؟",
      answerEn: "RAQIB is specifically designed for healthcare clinics with features like patient management, appointment scheduling, medical records, and secure data handling. Our platform combines ease of use with enterprise-grade security, making it ideal for both small clinics and large medical facilities.",
      answerFr: "RAQIB est spécialement conçu pour les cliniques de santé avec des fonctionnalités telles que la gestion des patients, la planification des rendez-vous, les dossiers médicaux et le traitement sécurisé des données. Notre plateforme combine la facilité d'utilisation avec une sécurité de niveau entreprise.",
      answerAr: "تم تصميم RAQIB خصيصًا لعيادات الرعاية الصحية مع ميزات مثل إدارة المرضى وجدولة المواعيد والسجلات الطبية والتعامل الآمن مع البيانات. تجمع منصتنا بين سهولة الاستخدام والأمان على مستوى المؤسسة."
    },
    {
      questionEn: "How secure are my conversations?",
      questionFr: "À quel point mes conversations sont-elles sécurisées?",
      questionAr: "ما مدى أمان محادثاتي؟",
      answerEn: "Your data is protected with military-grade encryption (AES-256), multi-factor authentication, and HIPAA-compliant architecture. All communications are end-to-end encrypted, and access logs track every interaction with patient data. We perform regular security audits and penetration testing.",
      answerFr: "Vos données sont protégées par un chiffrement de qualité militaire (AES-256), une authentification multifacteur et une architecture conforme HIPAA. Toutes les communications sont chiffrées de bout en bout, et les journaux d'accès suivent chaque interaction avec les données des patients.",
      answerAr: "يتم حماية بياناتك باستخدام تشفير عسكري (AES-256) والمصادقة متعددة العوامل والعمارة المتوافقة مع HIPAA. يتم تشفير جميع الاتصالات من طرف إلى طرف، وتتبع سجلات الوصول كل تفاعل مع بيانات المريض."
    },
    {
      questionEn: "Can I personalize my RAQIB experience?",
      questionFr: "Puis-je personnaliser mon expérience RAQIB?",
      questionAr: "هل يمكنني تخصيص تجربة RAQIB الخاصة بي؟",
      answerEn: "Absolutely! RAQIB allows extensive customization including clinic branding, custom workflows, user role management, and personalized dashboards. You can configure appointment types, patient forms, reminder messages, and notification preferences to match your clinic's specific needs.",
      answerFr: "Absolument! RAQIB permet une personnalisation extensive, y compris la marque de la clinique, les flux de travail personnalisés, la gestion des rôles des utilisateurs et les tableaux de bord personnalisés. Vous pouvez configurer les types de rendez-vous, les formulaires pour patients, les messages de rappel et les préférences de notification.",
      answerAr: "بالتأكيد! يسمح RAQIB بتخصيص شامل بما في ذلك علامة العيادة التجارية وسير العمل المخصص وإدارة أدوار المستخدمين والمراقب المخصص. يمكنك تكوين أنواع المواعيد ونماذج المريض ورسائل التذكير وتفضيلات الإخطارات."
    },
    {
      questionEn: "What features does RAQIB offer?",
      questionFr: "Quelles fonctionnalités RAQIB offre-t-il?",
      questionAr: "ما هي الميزات التي يوفرها RAQIB؟",
      answerEn: "RAQIB includes patient management, appointment scheduling, medical records, billing & invoicing, staff management, real-time notifications, analytics & reporting, prescription management, secure messaging, and integration with external systems. All features are designed with user experience and compliance in mind.",
      answerFr: "RAQIB comprend la gestion des patients, la planification des rendez-vous, les dossiers médicaux, la facturation, la gestion du personnel, les notifications en temps réel, l'analyse et les rapports, la gestion des ordonnances, la messagerie sécurisée et l'intégration avec les systèmes externes.",
      answerAr: "يشمل RAQIB إدارة المرضى وجدولة المواعيد والسجلات الطبية والفواتير والمحاسبة وإدارة الموظفين والإخطارات في الوقت الفعلي والتحليلات والتقارير وإدارة الوصفات الطبية والمراسلة الآمنة والتكامل مع الأنظمة الخارجية."
    }
  ];

  return (
    <div className="min-h-screen font-sans bg-white pb-20" dir={isAr ? "rtl" : "ltr"}>
      {/* Hero Contact Section */}
      <section className="bg-gradient-to-b from-blue-50 via-indigo-50 to-purple-50 pt-20 pb-20 md:pb-32 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-24 items-start">
            
            {/* Left Info Column */}
            <FadeUp className="pt-8">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-6 tracking-tight">
                {isAr ? "اتصل بنا" : isFr ? "Contactez-nous" : "Contact Us"}
              </h1>
              <p className="text-gray-600 text-lg mb-8 max-w-sm leading-relaxed">
                {isAr
                  ? "أرسل بريداً إلكترونياً، أو اتصل، أو أكمل النموذج لمعرفة كيف يمكننا حل مشكلة المراسلة لديك."
                  : isFr
                  ? "Envoyez un e-mail, appelez ou remplissez le formulaire pour découvrir comment nous pouvons résoudre vos problèmes de messagerie."
                  : "Email, call, or complete the form to learn how RAQIB can solve your messaging problem."}
              </p>

              <div className="space-y-3 mb-8">
                <p className="text-gray-800 font-medium">info@raqibapp.com</p>
                <p className="text-gray-800 font-medium">321-221-231</p>
                <a href="#support" className="inline-block text-gray-900 font-medium underline underline-offset-4 mt-2">
                  {isAr ? "دعم العملاء" : isFr ? "Support client" : "Customer Support"}
                </a>
              </div>

              {/* Support 3-column Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {isAr ? "دعم العملاء" : isFr ? "Support client" : "Customer Support"}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {isAr ? "فريقنا متاح على مدار الساعة للإجابة على أي استفسارات قد تكون لديكم." : isFr ? "Notre équipe est disponible 24h/24 pour répondre à toutes vos questions." : "Our support team is available around the clock to address any concerns or queries you may have."}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {isAr ? "الملاحظات والاقتراحات" : isFr ? "Retours et suggestions" : "Feedback and Suggestions"}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {isAr ? "نحن نقدر ملاحظاتك ونعمل باستمرار على تحسين خدماتنا." : isFr ? "Nous apprécions vos retours et travaillons à l'amélioration de nos services." : "We value your feedback and are continuously working to improve RAQIB."}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {isAr ? "استفسارات إعلامية" : isFr ? "Demandes des médias" : "Media Inquiries"}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {isAr ? "للاستفسارات الصحفية، يرجى التواصل معنا عبر البريد." : isFr ? "Pour les questions liées aux médias, contactez-nous par e-mail." : "For media-related questions or press inquiries, please contact us at media@raqibapp.com."}
                  </p>
                </div>
              </div>
            </FadeUp>

            {/* Right Card Column */}
            <FadeUp delay={0.2} className="bg-white rounded-[2rem] shadow-xl p-6 md:p-8 lg:p-12 border border-gray-100">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold mb-2 text-gray-900">
                {isAr ? "تواصل معنا" : isFr ? "Prendre contact" : "Get in Touch"}
              </h2>
              <p className="text-gray-500 mb-8">
                {isAr ? "يمكنك التواصل معنا في أي وقت" : isFr ? "Vous pouvez nous joindre à tout moment" : "You can reach us anytime"}
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition placeholder-gray-400"
                    placeholder={isAr ? "الاسم الأول" : isFr ? "Prénom" : "First name"}
                  />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition placeholder-gray-400"
                    placeholder={isAr ? "اسم العائلة" : isFr ? "Nom" : "Last name"}
                  />
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full ${isAr ? 'pr-11 pl-4' : 'pl-11 pr-4'} py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition placeholder-gray-400`}
                    placeholder={isAr ? "بريدك الإلكتروني" : isFr ? "Votre e-mail" : "Your email"}
                  />
                </div>

                <div className="flex border border-gray-200 rounded-xl focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition overflow-hidden bg-white">
                  <div className="flex items-center px-4 bg-white border-r border-gray-200 text-gray-600 font-medium whitespace-nowrap" dir="ltr">
                    +212
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white focus:outline-none placeholder-gray-400"
                    placeholder={isAr ? "رقم الهاتف" : isFr ? "Numéro de téléphone" : "Phone number"}
                  />
                </div>

                <div className="relative">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition resize-none placeholder-gray-400"
                    placeholder={isAr ? "كيف يمكننا المساعدة؟" : isFr ? "Comment pouvons-nous vous aider ?" : "How can we help?"}
                  ></textarea>
                  <div className={`absolute bottom-3 ${isAr ? 'left-3' : 'right-3'} text-xs text-gray-400`}>
                    {formData.message.length}/120
                  </div>
                </div>

                {message && (
                  <div className={`p-3 rounded-lg text-sm font-medium ${message.startsWith("✓") ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                    {message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || cooldownRemaining > 0}
                  className="w-full py-4 bg-[#1a66ff] hover:bg-blue-700 disabled:bg-blue-300 active:scale-95 text-white font-medium rounded-xl transition-all duration-200"
                >
                  {cooldownRemaining > 0
                    ? (language === "ar" 
                      ? `⏳ انتظر ${Math.ceil(cooldownRemaining / 1000)}ث`
                      : language === "fr"
                      ? `⏳ Attendez ${Math.ceil(cooldownRemaining / 1000)}s`
                      : `⏳ Wait ${Math.ceil(cooldownRemaining / 1000)}s`)
                    : loading
                    ? (isAr ? "جاري الإرسال..." : isFr ? "Envoi..." : "Sending...")
                    : (isAr ? "إرسال" : isFr ? "Soumettre" : "Submit")}
                </button>
                
                <p className="text-center text-xs text-gray-500 mt-4 leading-relaxed">
                  {isAr ? "بالتواصل معنا، فإنك توافق على " : isFr ? "En nous contactant, vous acceptez nos " : "By contacting us, you agree to our "}
                  <button type="button" className="font-semibold text-gray-700 hover:text-blue-600 transition underline">{isAr ? "شروط الخدمة" : isFr ? "Conditions d'utilisation" : "Terms of service"}</button>
                  {isAr ? " و " : isFr ? " et " : " and "}
                  <button type="button" className="font-semibold text-gray-700 hover:text-blue-600 transition underline">{isAr ? "سياسة الخصوصية" : isFr ? "Politique de confidentialité" : "Privacy Policy"}</button>
                </p>
              </form>
            </FadeUp>
            
          </div>
        </div>
      </section>

      {/* Map / Location Section */}
      <section className="bg-gradient-to-b from-white via-slate-50 to-blue-50 py-20 px-4">
        <FadeUp>
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            <div className="bg-blue-50 rounded-[2rem] aspect-square md:aspect-[4/3] flex items-center justify-center relative overflow-hidden border border-blue-100 shadow-lg">
            {/* Visual Map Placeholder mimicking the design */}
            <div className="absolute inset-0 opacity-40 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] bg-blue-100"></div>
            
            <div className="relative bg-white p-5 rounded-2xl shadow-xl max-w-[240px] z-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">R</div>
                <div>
                  <h4 className="font-semibold text-sm">RAQIB Inc.</h4>
                  <p className="text-[10px] text-gray-500">Beyond Limits Together</p>
                </div>
              </div>
              <p className="font-medium text-xs">San Francisco, USA</p>
              <p className="text-[10px] text-gray-500 mb-3">123 Tech Boulevard, Suite 456</p>
              <button type="button" className="w-full flex items-center justify-between text-xs font-medium text-gray-900 border-t pt-3 hover:text-blue-600 transition">
                Open Google Maps
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
              </button>
            </div>
          </div>
          
          <div className="max-w-md">
            <p className="text-sm font-semibold text-gray-900 mb-2">
              {isAr ? "موقعنا" : isFr ? "Notre emplacement" : "Our Location"}
            </p>
            <h2 className="text-4xl font-semibold mb-10 tracking-tight">
              {isAr ? "نربطك بالقريب والبعيد" : isFr ? "Connecter de près et de loin" : "Connecting Near and Far"}
            </h2>
            
            <h3 className="text-xl font-semibold mb-3">
              {isAr ? "المقر الرئيسي" : isFr ? "Siège social" : "Headquarters"}
            </h3>
            <div className="text-gray-600 text-sm space-y-1">
              <p>RAQIB Inc.</p>
              <p>San Francisco, USA</p>
              <p>123 Tech Boulevard, Suite 456</p>
              <p>San Francisco, CA 12345</p>
              <p>United States</p>
            </div>
          </div>
        </div>
        </FadeUp>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <FadeUp>
            <div className="grid md:grid-cols-2 gap-16 items-start">
              <div>
                <p className="text-sm font-semibold text-gray-900 mb-2">FAQ</p>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold mb-6 tracking-tight max-w-sm">
                  {isAr ? "هل لديك أي أسئلة لنا؟" : isFr ? "Avez-vous des questions pour nous ?" : "Do you have any questions for us?"}
                </h2>
                <p className="text-gray-500 mb-8 max-w-sm">
                  {isAr ? "إذا كانت هناك أسئلة تود طرحها. سنجيب على جميع تساؤلاتك." : isFr ? "S'il y a des questions que vous souhaitez poser. Nous répondrons à toutes vos questions." : "If there are questions you want to ask. We will answer all your questions."}
                </p>
                
                <div className="flex gap-2 max-w-sm">
                  <div className="relative flex-1">
                    <div className={`absolute inset-y-0 ${isAr ? 'right-0 pr-4' : 'left-0 pl-4'} flex items-center pointer-events-none transition-colors ${emailInput ? 'text-blue-500' : 'text-gray-400'}`}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                      </svg>
                    </div>
                    <input 
                      type="email" 
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      placeholder={isAr ? "أدخل بريدك الإلكتروني" : isFr ? "Entrez votre email" : "Enter your email"}
                      className={`w-full ${isAr ? 'pr-11 pl-4' : 'pl-11 pr-4'} py-3 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-blue-50 focus:shadow-md transition-all placeholder-gray-400 font-medium`}
                    />
                  </div>
                  <button 
                    type="button"
                    onClick={() => {
                      if (emailInput.trim()) {
                        window.location.href = `mailto:contact@mail.raqibapp.com?to=${emailInput}`;
                      } else {
                        alert(isAr ? "يرجى إدخال بريدك الإلكتروني" : isFr ? "Veuillez entrer votre email" : "Please enter your email");
                      }
                    }}
                    className="px-6 py-3 bg-[#1a66ff] hover:bg-blue-700 active:scale-95 text-white font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 whitespace-nowrap"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                    </svg>
                    {isAr ? "إرسال" : isFr ? "Soumettre" : "Submit"}
                  </button>
                </div>
              </div>

              <div className="space-y-3 pt-12 md:pt-0">
                {faqItems.map((item, idx) => {
                  const question = language === "ar" ? item.questionAr : language === "fr" ? item.questionFr : item.questionEn;
                  const answer = language === "ar" ? item.answerAr : language === "fr" ? item.answerFr : item.answerEn;
                  const isExpanded = expandedFaq === idx;

                  return (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl overflow-hidden border border-gray-100 hover:border-blue-200 transition-all"
                    >
                      <button
                        onClick={() => setExpandedFaq(isExpanded ? null : idx)}
                        className="w-full flex justify-between items-center p-5 hover:bg-gradient-to-r hover:from-blue-100 hover:to-indigo-100 transition-all text-left"
                      >
                        <span className="font-semibold text-gray-900 pr-4">{question}</span>
                        <motion.svg
                          animate={{ rotate: isExpanded ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                          className="w-5 h-5 text-blue-600 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </motion.svg>
                      </button>

                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={isExpanded ? { opacity: 1, height: "auto" } : { opacity: 0, height: 0 }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-5 pt-0 text-gray-700 text-sm leading-relaxed border-t border-blue-100">
                          {answer}
                        </div>
                      </motion.div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}

export default Contact;
